import { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  downloadInventoryControlRegistries,
  editReasonAndSolution,
  getInventoryControlRegisters,
  getMeasurementListJson,
  getPageableInventoryControlRegistriesByPeriod,
  reCalculateInventoryControl,
  RequestEditInventoryControlRegister,
  ResponseInventoryControlRegistry,
} from "api/generated";
import { useClient } from "hooks/useClient";
import { RequestGetInventoryControlDetails } from "api/generated";
import { message, notification, Progress, Statistic } from "antd";
import { isAxiosError } from "axios";
import moment from "moment";
import DistributionPointSelected from "context/MyDistributionPoint";

interface UseStockControlProps {
  idSubproduct?: number;
  startDate: string;
  endDate: string;
  fetchByDay: boolean;
}

export const useStockControl = ({
  idSubproduct = 51,
  startDate,
  endDate,
  fetchByDay,
}: UseStockControlProps) => {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [registries, setRegistries] = useState<
    RequestGetInventoryControlDetails[] | ResponseInventoryControlRegistry[]
  >([]);
  const [registriesByPeriod, setRegistriesByPeriod] = useState<
    ResponseInventoryControlRegistry[]
  >([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Referencias para los AbortController de cada petición
  const fetchStockControllerRef = useRef<AbortController | null>(null);
  const fetchStockByPeriodControllerRef = useRef<AbortController | null>(null);
  const downloadControllerRef = useRef<AbortController | null>(null);

  /**
   * Obtiene los registros de control de inventario por día
   */
  const fetchStockControl = useCallback(() => {
    if (!distributionPoint?.id || !fetchByDay) {
      return;
    }
    // Cancelar petición anterior si existe
    if (fetchStockControllerRef.current) {
      fetchStockControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    fetchStockControllerRef.current = controller;

    setLoading(true);
    setError(undefined);
    getInventoryControlRegisters({
      client,
      body: {
        requestInventoryControlDisplayPage: currentPage,
        requestInventoryControlDisplayAmount: pageSize,
        idSubproduct,
        requestInventoryControlStartDate: startDate,
        requestInventoryControlEndDate: endDate,
        distributionPointIds: [distributionPoint.id],
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        setRegistries(data?.details ?? []);
        setTotalItems(data?.inventoryControlTotalRecords ?? 0);
        setTotalPages(
          Math.ceil((data?.inventoryControlTotalRecords ?? 0) / pageSize)
        );
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          setError(`${err}`);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    client,
    idSubproduct,
    currentPage,
    pageSize,
    startDate,
    endDate,
    fetchByDay,
    distributionPoint,
    refreshTrigger,
  ]);

  /**
   * Obtiene los registros de control de inventario por período
   */
  const fetchStockControlByPeriod = useCallback(() => {
    if (!distributionPoint?.id || fetchByDay) {
      return;
    }

    // Cancelar petición anterior si existe
    if (fetchStockByPeriodControllerRef.current) {
      fetchStockByPeriodControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    fetchStockByPeriodControllerRef.current = controller;

    setLoading(true);
    setError(undefined);
    getPageableInventoryControlRegistriesByPeriod({
      client,
      query: {
        startDate,
        finalDate: endDate,
        distributionPoints: [distributionPoint.id],
        page: currentPage,
        size: pageSize,
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        setRegistriesByPeriod(data?.content ?? []);
        setTotalItems(data?.totalElements ?? 0);
        setTotalPages(data?.totalPages ?? 0);
      })
      .catch((err) => {
        console.error("Error fetching stock control:", err);
        setError(`${err}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    client,
    idSubproduct,
    currentPage,
    pageSize,
    startDate,
    endDate,
    fetchByDay,
    distributionPoint,
    refreshTrigger,
  ]);

  /**
   * Cambia la página actual de los resultados paginados
   * @param {number} page - Número de página a establecer
   */
  function setPage(page: number) {
    setCurrentPage(page - 1);
  }

  /**
   * Actualiza el tamaño de página para la paginación de resultados
   * @param {number} size - Nuevo tamaño de página
   * reinicia la página actual a 0
   */
  function updatePageSize(size: number) {
    setPageSize(size);
    setCurrentPage(0);
  }

  // Efecto para resetear la pagina cuando cambian el punto de distribucion
  useEffect(() => {
    setCurrentPage(0);
  }, [distributionPoint?.id]);

  // Efecto para cargar datos cuando cambian las dependencias
  useEffect(() => {
    fetchStockControl();
    fetchStockControlByPeriod();
  }, [
    distributionPoint?.id,
    startDate,
    endDate,
    currentPage,
    pageSize,
    fetchByDay,
    refreshTrigger,
  ]);

  /**
   * Recalcula el control de existencias (GIC) para una fecha específica
   * @param {moment.Moment} dateToReCalculate - Fecha para la cual se recalculará el inventario
   * @param {number} [desiredInitialVolume] - Volumen inicial deseado (opcional)
   * Muestra notificaciones de éxito/error
   */
  const recalculateGic = useCallback(
    (dateToReCalculate: moment.Moment, desiredInitialVolume?: number) => {
      if (!distributionPoint?.id) {
        return;
      }
      if (
        desiredInitialVolume !== undefined &&
        Number(desiredInitialVolume) <= 0
      ) {
        message.error("El volumen inicial debe de ser mayor a cero");
        return;
      }
      setLoading(true);
      const date = dateToReCalculate.format("YYYY-MM-DD");
      reCalculateInventoryControl({
        client,
        body: {
          startDate: date,
          reportType: "Regeneración de GIC",
          desiredInitialVolume,
          idSubproduct: 51,
          distributionPointId: distributionPoint.id,
        },
      })
        .then(() => {
          notification.success({
            message: `El calculo de existencias para el punto de distribución ${distributionPoint.name} 
          en la fecha ${date} 
          ha sido actualizado exitosamente`,
          });
        })
        .catch((err) => {
          let error = `${err}`;
          if (isAxiosError(err)) {
            error = err.response?.data.message;
          }
          message.error(error);
        })
        .finally(() => {
          setLoading(false);
          setRefreshTrigger((prev) => prev + 1);
        });
    },
    [distributionPoint, client, setRefreshTrigger]
  );

  /**
   * Recalcula el control de existencias (GIC) para un período completo (todos los días del mes)
   * @param {moment.Moment} periodDate - Fecha del período (mes) a recalcular
   */
  const recalculatePeriod = useCallback(
    async (periodDate: moment.Moment) => {
      if (!distributionPoint?.id) {
        return;
      }

      setLoading(true);
      const notificationKey = `recalculate-progress-key`;
      try {
        // Obtener todos los días del mes
        const startOfMonth = periodDate.clone().startOf("month");
        const endOfMonth = periodDate.clone().endOf("month");
        const currentDate = moment();
        let daysInMonth = endOfMonth.date();

        // Si es el mes y año actual, solo regenerar hasta el día actual
        const isCurrentMonth =
          periodDate.isSame(currentDate, "month") &&
          periodDate.isSame(currentDate, "year");

        if (isCurrentMonth) {
          daysInMonth = currentDate.date();
        }

        let successfulRecalculations = 0;
        let failedRecalculations = 0;

        // Procesar cada día del mes
        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = startOfMonth.clone().date(day);
          const dateStr = currentDate.format("YYYY-MM-DD");

          // Actualizar progreso
          notification.open({
            key: notificationKey,
            message: `Regenerando el período ${periodDate.format("YYYY-MM")}`,
            description: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Progress
                  type="circle"
                  percent={Number(((day * 100) / daysInMonth).toFixed(0))}
                  status={failedRecalculations === 0 ? "normal" : "exception"}
                />
                <Statistic
                  title="Ejecutando control de existencias"
                  value={day}
                  suffix={`/  ${daysInMonth}`}
                />
              </div>
            ),
            duration: 0,
          });

          try {
            await reCalculateInventoryControl({
              client,
              body: {
                startDate: dateStr,
                reportType: "Regeneración de GIC",
                idSubproduct: 51,
                distributionPointId: distributionPoint.id,
              },
            });

            successfulRecalculations++;
          } catch (err) {
            failedRecalculations++;
            console.error(`Error recalculating GIC for ${dateStr}:`, err);
          }
        }

        // Cerrar notificación de progreso
        notification.destroy(notificationKey);

        if (failedRecalculations > 0) {
          notification.warning({
            message: `No fue posible procesar la regeneración del período`,
            description: `No se procesaron los controles de existencia para: ${failedRecalculations} días.
          Visualiza los controles de existencia diarios para identificar el problema.
          `,
          });
        } else {
          notification.info({
            message: `Regeneración de período completada`,
            description: `Los controles de existencia para ${successfulRecalculations} días fureron recalculados`,
          });
        }

        // Recargar datos
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        notification.destroy(notificationKey);
        notification.error({
          message: `Error al procesar el período ${error}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [distributionPoint, client, setRefreshTrigger]
  );

  /**
   * Actualiza o inserta la causa y solución para una diferencia de inventario
   * @param {RequestEditInventoryControlRegister} request - Objeto con la información a actualizar
   * Muestra notificaciones de éxito/error
   */
  function upsertCauseAndSolutionOnDifference(
    request: RequestEditInventoryControlRegister
  ) {
    setLoading(true);
    editReasonAndSolution({
      client,
      body: request,
    })
      .then(() => {
        notification.success({
          message: `Información de la inconsistencia guardada exitosamente`,
        });
      })
      .catch((err) => {
        let error = `${err}`;
        if (isAxiosError(err)) {
          error = err.response?.data.message;
        }
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
        setRefreshTrigger((prev) => prev + 1);
      });
  }

  /**
   * Obtiene la última medición para un control de existencias específico
   * @param {string} wareHouseInventoryControlId - ID del control de inventario
   * @returns {Promise<MeasurementTank>} Promesa que resuelve con un array de entradas de medición
   * @throws {Error} Lanza un error si la petición falla
   */
  async function getLastMeasurement(wareHouseInventoryControlId: string) {
    try {
      const data = await getMeasurementListJson({
        client,
        path: {
          wareHouseInventoryControlId,
        },
      });
      return data?.data?.entries ?? [];
    } catch (err) {
      let error = `${err}`;
      if (isAxiosError(err)) {
        error = err.response?.data.message;
      }
      message.error(error);
      throw new Error(error);
    }
  }

  /**
   * Descarga los registros de control de existencias
   */
  const downloadStockControl = useCallback(() => {
    if (!distributionPoint?.id) {
      return;
    }

    // Cancelar petición anterior si existe
    if (downloadControllerRef.current) {
      downloadControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    downloadControllerRef.current = controller;

    setLoading(true);
    setError(undefined);
    downloadInventoryControlRegistries({
      client,
      query: {
        startDate,
        finalDate: endDate,
        distributionPoints: [distributionPoint.id],
        byDay: fetchByDay,
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        if (data) {
          const url = window.URL.createObjectURL(data as unknown as Blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `control_existencias_${
              fetchByDay ? "diario" : "mensual"
            }_${distributionPoint.name?.replace(/\s/g, "_")}_${
              startDate.split(" ")[0]
            }_${endDate.split(" ")[0]}.csv`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          setError(`${err}`);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    client,
    idSubproduct,
    currentPage,
    pageSize,
    startDate,
    endDate,
    fetchByDay,
    distributionPoint,
    refreshTrigger,
  ]);

  return {
    // Estado
    registries,
    registriesByPeriod,
    currentPage: currentPage + 1,
    pageSize,
    totalItems,
    totalPages,
    loading,
    error,

    // Acciones
    setPage,
    updatePageSize,
    recalculateGic,
    recalculatePeriod,
    upsertCauseAndSolutionOnDifference,
    getLastMeasurement,
    downloadStockControl,
  };
};
